import React from 'react';
import { parseImageData, isLocationGuidePage, getLocationGuideCtas } from '@greystar/lib';
import {
  FlexibleHeroNoImage,
  FlexibleHeroNoText,
  FlexibleHero,
  HeroImageProps,
  FlexibleHeroProps,
} from '@greystar/ui';
import Image from 'next/image';
import { RichText as JssRichText, Text as JssText } from '@sitecore-jss/sitecore-jss-nextjs';
import { SxaTag } from '@greystar/ui/src/components/FlexibleHero/FlexibleHero.props';
import { TextColorProps } from '@greystar/lib/src/types/style-types';

const getHeroElements = (props: HeroImageProps): FlexibleHeroProps => {
  let image: string | undefined;
  let headingData: React.ReactElement | undefined;
  let titleData: React.ReactElement | undefined;
  const templateId = (props.sitecoreContext?.route?.templateId ?? '').toUpperCase();
  const locationGuidePage = isLocationGuidePage(templateId);
  const editorialImage =
    (props.sitecoreContext?.route?.fields?.['Tile Image AEM'] as { value: string })?.value ??
    (props.sitecoreContext?.route?.fields?.['Image'] as { value: string })?.value;

  const pageHeading =
    (props.sitecoreContext?.route?.fields?.['Import Name'] as { value: string })?.value ??
    (props.sitecoreContext?.route?.fields?.['Name'] as { value: string })?.value;

  const pageHeadline = props.sitecoreContext?.route?.fields?.['Headline'] as {
    value: string;
  };

  if (props.fields?.image) {
    image = props.fields.image.value;
  } else if (locationGuidePage) {
    image = editorialImage;
  }

  if (props.fields?.heading) {
    headingData = <JssText field={props.fields.heading} />;
  } else if (pageHeading) {
    headingData = <>{pageHeading}</>;
  }

  if (props.fields?.title) {
    titleData = <JssText field={props.fields.title} />;
  } else if (pageHeadline?.value) {
    titleData = <>{pageHeadline.value}</>;
  }

  const parsedImage = image ? parseImageData(image) : null;
  const imageElement = parsedImage ? (
    <Image alt={parsedImage?.alt ?? ''} loading="lazy" fill src={parsedImage.src ?? ''} />
  ) : null;

  const title = titleData ?? null;
  const heading = headingData ?? null;
  const bodyText = props.fields?.bodyText ? <JssRichText field={props.fields.bodyText} /> : null;
  const tags =
    props.params?.showTags === '1'
      ? getLocationGuideCtas(props.sitecoreContext?.route?.fields?.SxaTags as SxaTag[] | undefined)
      : [];

  return {
    image: imageElement,
    title,
    heading,
    bodyText,
    SxaTags: tags,
    isLocationGuidePage: locationGuidePage,
    reverse: props.params?.reverse === '1' ? 'reverse' : '',
    bodyTextIndentation: props.params?.bodyTextIndentation === '1',
    headingColor: props.params?.headingColor as TextColorProps,
    titleColor: props.params?.titleColor as TextColorProps,
    icon: props.params.icon,
    backgroundColours: props.params.backgroundColours,
    headingStyles: props.params?.headingStyles ?? undefined,
  };
};

const FlexibleHeroDefault = (props: HeroImageProps): JSX.Element => {
  return <FlexibleHero {...getHeroElements(props)} />;
};

export const WithoutImage = (props: HeroImageProps): JSX.Element => {
  return <FlexibleHeroNoImage {...getHeroElements(props)} />;
};

export const WithoutText = (props: HeroImageProps): JSX.Element => {
  return <FlexibleHeroNoText {...getHeroElements(props)} />;
};

export default FlexibleHeroDefault;
